/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { useState } from 'react';
import moment from 'moment';
import { UserProfession } from '../../../../../enums/UserProfession';
import MagnifierIcon from '../../../../../images/magnifier.svg';
import { states } from '../../../../commonComponents/StatePicker';
import { StateCredentialFileV2 } from './StateCredentialFileV2';
import { ConfirmedNameSection } from './ConfirmedNameSection';
import { showErrorMessage } from '../../../../../utils';
import CustomPortal from '../../../../commonComponents/CustomPortal';
import { V2Type, getStatusConfig, isConfirmedNameCredential } from './credentialV2Utils';
import { useCredentialForm } from './hooks/useCredentialForm';

const FieldError = ({ message }) => {
  if (!message) return null;
  return (
    <p data-field-error="true" style={{ color: '#d32f2f', fontSize: 12, margin: '4px 0 0' }}>
      {message}
    </p>
  );
};

export const AuthorizationCredentialDetail = ({
  authorization,
  allProfessions,
  onSave,
  onDelete,
  monitoredCredentials = [],
}) => {
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const {
    editMode,
    auth,
    credentials,
    confirmedName,
    attested,
    setAttested,
    handleEdit,
    handleCancel,
    handleSave,
    updateCred,
    updateConfirmedName,
    updateReferenceByIndex,
    getFieldError,
    setNoExpiration,
    getNoExpiration,
  } = useCredentialForm({ authorization, onSave });

  const { profession, state: authState, status } = auth || {};

  const professionName =
    allProfessions?.find((p) => p.code === profession)?.name ||
    UserProfession.getName(profession) ||
    profession;

  const derivedStatus = status;

  const statesFiltered = states.filter((s) => s.abbreviation === authState);
  const licVerificationUrl = statesFiltered.length > 0 ? statesFiltered[0].licVerificationUrl : '';

  const allAttested = attested;

  const renderInfoCard = () => (
    <div
      className="card"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '5px 0',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 10px',
          borderBottom: '1px solid #efefef',
        }}
      >
        <div>License Status:</div>
        <div style={{ color: getStatusConfig(derivedStatus).color }}>
          {getStatusConfig(derivedStatus).label}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 10px',
          borderBottom: '1px solid #efefef',
        }}
      >
        <div>State:</div>
        <div>{authState}</div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 10px',
          borderBottom: '1px solid #efefef',
        }}
      >
        <div>Profession:</div>
        <div>{professionName}</div>
      </div>
    </div>
  );

  // One card per authorization: the state licence, or the DA/EFDA certification.
  const renderConfirmedNameSection = (cred) =>
    isConfirmedNameCredential(cred) && (
      <ConfirmedNameSection
        editMode={editMode}
        confirmedName={confirmedName}
        onChange={updateConfirmedName}
      />
    );

  const renderNumberFields = (cred) => {
    const numberData = cred?.data?.number;
    const credNumberError = getFieldError(cred, 'credential_number');
    const issuedError = getFieldError(cred, 'issued_at');
    const expiredError = getFieldError(cred, 'expired_at');

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: 40,
        }}
      >
        <div style={{ width: '50%', marginTop: 20, marginBottom: 20 }}>
          <div
            style={{
              width: '100%',
              height: 40,
              border: `1px solid ${credNumberError ? '#d32f2f' : '#efefef'}`,
              display: 'flex',
              borderRadius: 5,
              padding: editMode ? 0 : 10,
            }}
          >
            {!editMode && <p>{numberData?.credential_number || '-'}</p>}
            {editMode && (
              <input
                style={{ width: '100%' }}
                value={numberData?.credential_number || ''}
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    if (!c.data) c.data = {};
                    if (!c.data.number) c.data.number = {};
                    c.data.number.credential_number = value;
                  });
                }}
              />
            )}
          </div>
          {editMode && <FieldError message={credNumberError} />}
        </div>

        <div
          style={{
            width: '50%',
            borderBottom: `1px solid ${issuedError ? '#d32f2f' : '#efefef'}`,
            padding: '5px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <p className="card-text" style={{ fontWeight: '600' }}>
            Issued Date
          </p>
          {!editMode && (
            <p className="card-text">
              {cred?.issued_at ? moment.utc(cred.issued_at).format('YYYY-MM-DD') : '-'}
            </p>
          )}
          {editMode && (
            <div>
              <input
                style={{ width: '100%', height: 40 }}
                className="form-controll"
                value={cred?.issued_at ? moment(cred.issued_at).format('YYYY-MM-DD') : ''}
                type="date"
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    c.issued_at = value;
                  });
                }}
              />
              <FieldError message={issuedError} />
            </div>
          )}
        </div>

        <div
          style={{
            width: '50%',
            borderBottom: `1px solid ${expiredError ? '#d32f2f' : '#efefef'}`,
            padding: '5px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <p className="card-text" style={{ fontWeight: '600' }}>
            Expiration Date
          </p>
          {!editMode && (
            <p className="card-text">
              {cred?.expired_at ? moment.utc(cred.expired_at).format('YYYY-MM-DD') : '-'}
            </p>
          )}
          {editMode && (
            <div>
              <input
                style={{ width: '100%', height: 40 }}
                className="form-controll"
                value={cred?.expired_at ? moment(cred.expired_at).format('YYYY-MM-DD') : ''}
                type="date"
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    c.expired_at = value;
                  });
                }}
              />
              <FieldError message={expiredError} />
            </div>
          )}
        </div>

        {(() => {
          if (!cred?.credential?.includes('state_license')) return null;
          const credMonitored = monitoredCredentials.find((mc) => mc.credential_id === cred.id);
          if (!credMonitored?.last_verified_at) return null;
          return (
            <div
              style={{
                width: '50%',
                borderBottom: '1px solid #efefef',
                padding: '5px 10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <p className="card-text" style={{ fontWeight: '600' }}>
                Last Date Checked
              </p>
              <p className="card-text">
                {moment(credMonitored.last_verified_at).format('MM/DD/YYYY')}
              </p>
            </div>
          );
        })()}

        {renderConfirmedNameSection(cred)}
      </div>
    );
  };

  const renderArtifactDateFields = (cred) => {
    const issued_at = cred?.issued_at;
    const expired_at = cred?.expired_at;
    const noExpiration = getNoExpiration(cred);
    const issuedError = getFieldError(cred, 'issued_at');
    const expiredError = getFieldError(cred, 'expired_at');

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginTop: 60,
        }}
      >
        <div
          style={{
            width: '50%',
            borderBottom: `1px solid ${issuedError ? '#d32f2f' : '#efefef'}`,
            padding: '5px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <p className="card-text" style={{ fontWeight: '600' }}>
            Issued Date
          </p>
          {!editMode && (
            <p className="card-text">
              {issued_at ? moment.utc(issued_at).format('YYYY-MM-DD') : '-'}
            </p>
          )}
          {editMode && (
            <div>
              <input
                style={{ width: '100%', height: 40 }}
                className="form-controll"
                value={issued_at ? moment(issued_at).format('YYYY-MM-DD') : ''}
                type="date"
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    c.issued_at = value;
                  });
                }}
              />
              <FieldError message={issuedError} />
            </div>
          )}
        </div>

        <div
          style={{
            width: '50%',
            borderBottom: `1px solid ${expiredError ? '#d32f2f' : '#efefef'}`,
            padding: '5px 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <p className="card-text" style={{ fontWeight: '600' }}>
            Expiration Date
          </p>
          {!editMode && (
            <p className="card-text">
              {expired_at ? moment.utc(expired_at).format('YYYY-MM-DD') : '-'}
            </p>
          )}
          {editMode && (
            <div>
              <input
                style={{
                  width: '100%',
                  height: 40,
                  opacity: noExpiration ? 0.7 : 1,
                }}
                className="form-controll"
                value={expired_at ? moment(expired_at).format('YYYY-MM-DD') : '-'}
                disabled={noExpiration}
                type={noExpiration ? 'text' : 'date'}
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    c.expired_at = value;
                  });
                }}
              />
              {!noExpiration && <FieldError message={expiredError} />}
            </div>
          )}
        </div>

        {editMode && (
          <div style={{ width: '50%', padding: '5px 10px' }}>
            <label
              htmlFor={`no-exp-${cred.id || cred.credential}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
              }}
            >
              <input
                id={`no-exp-${cred.id || cred.credential}`}
                type="checkbox"
                checked={noExpiration}
                onChange={(e) => setNoExpiration(cred, e.target.checked)}
              />
              <span style={{ fontSize: 13, marginTop: 8 }}>No expiration date</span>
            </label>
          </div>
        )}
      </div>
    );
  };

  const renderArtifactSection = (cred) => {
    const artifactFiles = cred?.data?.artifact?.files || [];
    const hasFiles = artifactFiles.length > 0;

    const renderFiles = () => {
      if (!editMode && !hasFiles) {
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '20px 0',
              color: '#899197',
            }}
          >
            No file uploaded
          </div>
        );
      }

      return (
        <StateCredentialFileV2
          editMode={editMode}
          files={artifactFiles}
          credentialType={cred.credential}
          onFilesChange={(newFiles) => {
            updateCred(cred, (c) => {
              if (!c.data) c.data = {};
              if (!c.data.artifact) c.data.artifact = {};
              c.data.artifact.files = newFiles;
            });
          }}
        />
      );
    };

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {renderFiles()}
        {renderArtifactDateFields(cred)}
        {renderConfirmedNameSection(cred)}
      </div>
    );
  };

  const renderNpiFields = (cred) => {
    const npiData = cred?.data?.npi;
    const npiError = getFieldError(cred, 'npi_number');

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: 40,
        }}
      >
        <div style={{ width: '50%', marginTop: 20, marginBottom: 20 }}>
          <div
            style={{
              width: '100%',
              height: 40,
              border: `1px solid ${npiError ? '#d32f2f' : '#efefef'}`,
              display: 'flex',
              borderRadius: 5,
              padding: editMode ? 0 : 10,
            }}
          >
            {!editMode && <p>{npiData?.npi_number || '-'}</p>}
            {editMode && (
              <input
                style={{ width: '100%' }}
                maxLength={10}
                value={npiData?.npi_number || ''}
                onChange={(e) => {
                  const { value } = e.target;
                  updateCred(cred, (c) => {
                    if (!c.data) c.data = {};
                    if (!c.data.npi) c.data.npi = {};
                    c.data.npi.npi_number = value;
                  });
                }}
              />
            )}
          </div>
          {editMode && <FieldError message={npiError} />}
        </div>
      </div>
    );
  };

  const renderCredentialContent = (cred, referenceIndex) => {
    if (cred.type === V2Type.Number) {
      return renderNumberFields(cred);
    }

    if (cred.type === V2Type.Artifact) {
      return renderArtifactSection(cred);
    }

    if (cred.type === V2Type.Npi) {
      return renderNpiFields(cred);
    }

    if (cred.type === V2Type.Reference) {
      const refData = cred?.data?.reference;
      const emailError = getFieldError(cred, 'contact_email');

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            paddingBottom: 40,
          }}
        >
          <div
            style={{
              border: '1px solid #efefef',
              width: '50%',
              borderRadius: 5,
              padding: editMode ? 0 : 10,
              marginTop: 20,
            }}
          >
            <p className="card-text" style={{ fontWeight: '600' }}>
              Office Name
            </p>
            <div style={{ display: 'flex' }}>
              {!editMode && <p>{refData?.office_name || '-'}</p>}
              {editMode && (
                <input
                  style={{ width: '100%' }}
                  value={refData?.office_name || ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    updateReferenceByIndex(referenceIndex, (c) => {
                      if (!c.data) c.data = {};
                      if (!c.data.reference) c.data.reference = {};
                      c.data.reference.office_name = value;
                    });
                  }}
                />
              )}
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${emailError ? '#d32f2f' : '#efefef'}`,
              width: '50%',
              borderRadius: 5,
              padding: editMode ? 0 : 10,
              marginTop: 20,
            }}
          >
            <p className="card-text" style={{ fontWeight: '600' }}>
              Contact Email
            </p>
            <div style={{ display: 'flex' }}>
              {!editMode && <p>{refData?.contact_email || '-'}</p>}
              {editMode && (
                <div style={{ width: '100%' }}>
                  <input
                    inputMode="email"
                    style={{ width: '100%' }}
                    value={refData?.contact_email || ''}
                    onChange={(e) => {
                      const { value } = e.target;
                      updateReferenceByIndex(referenceIndex, (c) => {
                        if (!c.data) c.data = {};
                        if (!c.data.reference) c.data.reference = {};
                        c.data.reference.contact_email = value;
                      });
                    }}
                  />
                  <FieldError message={emailError} />
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              width: '50%',
              borderBottom: '1px solid #efefef',
              padding: '5px 10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <p className="card-text" style={{ fontWeight: '600' }}>
              Start Date
            </p>
            {!editMode && (
              <p className="card-text">
                {refData?.start_date ? moment.utc(refData.start_date).format('YYYY-MM-DD') : '-'}
              </p>
            )}
            {editMode && (
              <input
                style={{ width: '70%', height: 40 }}
                className="form-controll"
                value={refData?.start_date ? moment(refData.start_date).format('YYYY-MM-DD') : ''}
                type="date"
                onChange={(e) => {
                  const { value } = e.target;
                  updateReferenceByIndex(referenceIndex, (c) => {
                    if (!c.data) c.data = {};
                    if (!c.data.reference) c.data.reference = {};
                    c.data.reference.start_date = value;
                  });
                }}
              />
            )}
          </div>

          <div
            style={{
              width: '50%',
              borderBottom: '1px solid #efefef',
              padding: '5px 10px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <p className="card-text" style={{ fontWeight: '600' }}>
              End Date
            </p>
            {!editMode && (
              <p className="card-text">
                {refData?.end_date ? moment.utc(refData.end_date).format('YYYY-MM-DD') : '-'}
              </p>
            )}
            {editMode && (
              <input
                style={{ width: '70%', height: 40 }}
                className="form-controll"
                value={refData?.end_date ? moment(refData.end_date).format('YYYY-MM-DD') : ''}
                type="date"
                onChange={(e) => {
                  const { value } = e.target;
                  updateReferenceByIndex(referenceIndex, (c) => {
                    if (!c.data) c.data = {};
                    if (!c.data.reference) c.data.reference = {};
                    c.data.reference.end_date = value;
                  });
                }}
              />
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCredentialStatusRow = (cred) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        borderBottom: '1px solid #efefef',
      }}
    >
      <div>License Status:</div>
      <div style={{ color: getStatusConfig(cred.status).color }}>
        {editMode ? (
          <select
            value={cred.status || ''}
            className="form-control"
            style={{ height: 32 }}
            onChange={(e) => {
              const { value } = e.target;
              updateCred(cred, (c) => {
                c.status = value;
              });
            }}
          >
            <option value="pending_verification">Pending Verification</option>
            <option value="verified">Verified</option>
            <option value="invalid">Invalid</option>
            <option value="expired">Expired</option>
          </select>
        ) : (
          getStatusConfig(cred.status).label
        )}
      </div>
    </div>
  );

  const getCredentialSubtitle = (cred) => {
    if (cred?.type === V2Type.Npi) {
      return 'Enter NPI Number';
    }
    if (cred?.type === V2Type.Number && cred?.credential?.includes('state_license')) {
      return 'Enter License Number';
    }
    if (cred?.type === V2Type.Artifact) {
      return `Example: ${cred.credential_name || ''}`;
    }
    return null;
  };

  return (
    <div style={{ padding: 10 }}>
      <div className="row" style={{ marginBottom: 10 }}>
        <div
          className="col-6 offset-3"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflowY: 'auto',
          }}
        >
          {renderInfoCard()}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 10 }}>
        {credentials.map((cred, index) => {
          const isNpi = cred?.type === V2Type.Npi;
          const isReference = cred?.type === V2Type.Reference;
          const referenceIndex = isReference
            ? credentials.filter((c, i) => c?.type === V2Type.Reference && i < index).length
            : 0;

          // Lookup link on every credential: NPI uses the national registry (same for
          // everyone), all others use the state licensing board. Clicking copies the
          // entered license number. Hidden only when the state has no board URL.
          const npiLookupUrl = 'https://npiregistry.cms.hhs.gov/search';
          const lookupUrl = isNpi ? npiLookupUrl : licVerificationUrl || null;
          const lookupCopyValue = isNpi
            ? cred?.data?.npi?.npi_number || ''
            : cred?.data?.number?.credential_number || '';

          return (
            <div
              key={cred.id || `${cred.credential}-${cred.type}-${index}`}
              className="col-6"
              style={{ marginBottom: 10 }}
            >
              <div
                className="card"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  paddingBottom: 40,
                }}
              >
                {renderCredentialStatusRow(cred)}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                  }}
                >
                  <div
                    className="card-body"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                      paddingTop: 25,
                    }}
                  >
                    <div
                      style={{
                        flex: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                      }}
                    >
                      <h4
                        className="card-title"
                        style={{
                          marginLeft: 40,
                          fontFamily: 'Nunito',
                          color: '#494963',
                        }}
                      >
                        {isReference ? cred.credential_name || 'Reference' : cred.credential_name}{' '}
                        <span
                          style={{
                            color: '#899197',
                            fontWeight: '300',
                          }}
                        >
                          {(() => {
                            const isRequired = isReference
                              ? referenceIndex === 0 && cred.is_required
                              : cred.is_required;
                            return isRequired ? 'Required(*)' : '(Optional)';
                          })()}
                        </span>
                      </h4>
                      {!isReference && getCredentialSubtitle(cred) && (
                        <p
                          style={{
                            color: '#899197',
                            fontSize: 14,
                            marginTop: 4,
                          }}
                        >
                          {getCredentialSubtitle(cred)}
                        </p>
                      )}
                    </div>

                    {lookupUrl && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <a
                          className="btn btn-secondary"
                          style={{ width: 94, height: 34 }}
                          href={lookupUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => {
                            const el = document.createElement('textarea');
                            el.value = lookupCopyValue;
                            document.body.appendChild(el);
                            el.select();
                            document.execCommand('copy');
                            document.body.removeChild(el);
                          }}
                        >
                          <img
                            src={MagnifierIcon}
                            alt="magnifier icon"
                            style={{ marginRight: 4, marginTop: -2 }}
                          />
                          Lookup
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {renderCredentialContent(cred, referenceIndex)}
              </div>
            </div>
          );
        })}

        <div
          className="col-12"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 20,
            marginTop: 20,
          }}
        >
          {!editMode ? (
            <>
              <button
                className="btn btn-info"
                style={{ width: 94, height: 34 }}
                onClick={handleEdit}
                type="button"
              >
                Edit
              </button>
              <button
                className="btn btn-danger"
                style={{ width: 94, height: 34 }}
                onClick={() => setShowDeletePopup(true)}
                type="button"
              >
                Delete
              </button>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <label
                htmlFor="attestation"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  cursor: 'pointer',
                  maxWidth: 500,
                }}
              >
                <input
                  id="attestation"
                  type="checkbox"
                  checked={attested}
                  onChange={(e) => setAttested(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span style={{ fontSize: 13 }}>
                  The credentials provided belong to the same professional that is listed on the
                  government ID of this account.
                </span>
              </label>
              <div style={{ display: 'flex', gap: 20 }}>
                <button
                  className="btn btn-success"
                  style={{ width: 94, height: 34 }}
                  onClick={() => {
                    if (!allAttested) {
                      showErrorMessage({
                        message:
                          'Confirm that the credentials provided belong to this professional.',
                        position: 'bottom-right',
                      });
                      return;
                    }
                    handleSave();
                  }}
                  type="button"
                >
                  Save
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ width: 94, height: 34 }}
                  onClick={handleCancel}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDeletePopup && (
        <CustomPortal
          title="Are you sure you want to delete this professional credential?"
          submitText="Delete"
          cancelText="Cancel"
          submitPressed={() => {
            onDelete?.(auth?.id);
            setShowDeletePopup(false);
          }}
          cancelPressed={() => setShowDeletePopup(false)}
        />
      )}
    </div>
  );
};
